import { MetricsDataController } from '../../../../controllers/metrics-controller.ts';
import '../../../../components/library/ui/app-card.ts';
import '../../../../components/library/ui/app-icon.ts';
import '../../../../components/library/ui/app-meter.ts';
import '../../../../components/library/ui/app-button.ts';
import '../../../../components/library/ui/charts/app-line-chart.ts';
import '../../../../components/library/ui/charts/app-chart-explorer.ts';

import '../../../../components/library/ui/app-time-picker.ts';


import { injectAesthetic } from '../../../../components/library/ui/shared-styles.ts';

export class GatewayMetrics extends HTMLElement {
  private _metricsController = MetricsDataController.getInstance();
  private _unsubscribe: (() => void) | null = null;
  private _orgId: string = '';
  private _timeRange: string = localStorage.getItem('crate_gateway_range') || '5m';
  private _isLoading: boolean = false;
  private _isInitialLoad: boolean = true;

  private _showCustomPicker: boolean = false;
  private _customRange = JSON.parse(localStorage.getItem('crate_custom_range') || JSON.stringify({
    start: new Date(Date.now() - 3600000).toISOString().slice(0, 16),
    end: new Date().toISOString().slice(0, 16)
  }));

  private _metrics = {
    traffic: '0',
    trafficHistory: [] as number[],
    trafficTrend: '+0%',
    latency: { min: '---', avg: '---', max: '---' },
    errorRate: '0%',
    dataUsage: { value: 0, max: 100, label: '0 B', sublabel: '' },
    trafficDetails: [] as any[],
  };

  private _methods: any[] = [];
  private _statusCodes: any[] = [];

  static get observedAttributes() {
    return ['org-id'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
  }

  attributeChangedCallback(name: string, oldVal: string, newVal: string) {
    if (name === 'org-id' && oldVal !== newVal) {
      this._orgId = newVal;
      if (this.isConnected) {
        this.loadMetrics();
        this._metricsController.startPolling();
      }
    }
  }

  connectedCallback() {
    this._orgId = this.getAttribute('org-id') || '';
    
    this._unsubscribe = this._metricsController.subscribe((data) => this.handleDataUpdate(data));

    this.render();
    if (this._orgId) {
      this.loadMetrics();
      this._metricsController.startPolling();
    }
  }

  disconnectedCallback() {
    if (this._unsubscribe) this._unsubscribe();
    this._metricsController.stopPolling();
  }

  private resetMetrics() {
    this._metrics = {
      traffic: '0',
      trafficHistory: [],
      trafficTrend: '+0%',
      latency: { min: '---', avg: '---', max: '---' },
      errorRate: '0%',
      dataUsage: { value: 0, max: 100, label: '0 B', sublabel: '' },
      trafficDetails: [],
    };
    this._methods = [];
    this._statusCodes = [];
  }

  async loadMetrics(showLoading = true) {
    if (showLoading) {
      this._isLoading = true;
      this.resetMetrics();
      this.render();
    }

    if (!this._orgId) return;

    let start: number | undefined;
    let end: number | undefined;

    if (this._timeRange === 'custom') {
      start = Math.floor(new Date(this._customRange.start).getTime() / 1000);
      end = Math.floor(new Date(this._customRange.end).getTime() / 1000);
    }

    this._metricsController.setParams(this._orgId, this._timeRange, start, end);
  }

  private handleDataUpdate(data: any) {
    if (!data) {
      this.resetMetrics();
      this.updateSurgicalUI();
      this._isLoading = false;
      this.render();
      return;
    }

    // Traffic
    this._metrics.traffic = data.traffic?.toLocaleString() || '0';
    const trend = data.traffic_trend || 0;
    this._metrics.trafficTrend = `${trend >= 0 ? '+' : ''}${trend.toFixed(1)}%`;

    // History
    if (Array.isArray(data.history)) {
      this._metrics.trafficHistory = data.history.map((h: any[]) => Number(h[1]));
    } else {
      this._metrics.trafficHistory = [];
    }

    // Latency
    this._metrics.latency = {
      min: data.latency_p50 !== undefined ? `${(data.latency_p50).toFixed(0)}ms` : '---', 
      avg: data.latency_avg !== undefined ? `${(data.latency_avg).toFixed(0)}ms` : '---', 
      max: data.latency_p99 !== undefined ? `${(data.latency_p99).toFixed(0)}ms` : '---'
    };

    // Error Rate
    const errRate = (data.error_rate || 0) * 100;
    this._metrics.errorRate = `${errRate.toFixed(2)}%`;

    // Data Usage
    const bytes = data.data_usage || 0;
    const units = ['B', 'KB', 'MB', 'GB', 'TB'];
    let unitIndex = 0;
    let val = bytes;
    while (val >= 1024 && unitIndex < units.length - 1) { val /= 1024; unitIndex++; }
    this._metrics.dataUsage = { 
      value: val, 
      max: Math.max(100, Math.pow(10, Math.ceil(Math.log10(val || 1)))), 
      label: `${val.toFixed(1)} ${units[unitIndex]}`, 
      sublabel: '' 
    };

    // Top Routes
    if (Array.isArray(data.routes)) {
      this._metrics.trafficDetails = data.routes.map((r: any) => ({
        method: r.method || '-',
        path: r.path || '-',
        status: String(r.status || '-'),
        rps: r.count.toLocaleString()
      }));
    } else {
      this._metrics.trafficDetails = [];
    }

    // Distributions
    this._methods = data.methods || [];
    this._statusCodes = data.status_codes || [];

    const wasInitialLoad = this._isInitialLoad;
    this._isInitialLoad = false;
    this._isLoading = false;
    
    if (wasInitialLoad) {
        this.render();
    } else {
        this.updateSurgicalUI();
    }
  }

  private updateSurgicalUI() {
    if (!this.shadowRoot) return;
    const container = this.shadowRoot;

    // Traffic
    const trafficVal = container.querySelector('#traffic-value');
    if (trafficVal) trafficVal.textContent = this._metrics.traffic;
    const trafficTrend = container.querySelector('#traffic-trend');
    if (trafficTrend) {
      trafficTrend.textContent = this._metrics.trafficTrend;
      trafficTrend.className = `trend ${parseFloat(this._metrics.trafficTrend) >= 0 ? 'up' : 'down'}`;
    }

    // Chart component
    const chart = container.querySelector('app-line-chart') as any;
    if (chart) {
        // Generate placeholder labels for tooltips/explorer based on length
        const labels = this._metrics.trafficHistory.map((_, i) => `T-${this._metrics.trafficHistory.length - i - 1}`);
        chart.chartData = { values: this._metrics.trafficHistory, labels };
    }

    // Latency
    const latMin = container.querySelector('#lat-min');
    if (latMin) latMin.textContent = this._metrics.latency.min;
    const latAvg = container.querySelector('#lat-avg');
    if (latAvg) latAvg.textContent = this._metrics.latency.avg;
    const latMax = container.querySelector('#lat-max');
    if (latMax) latMax.textContent = this._metrics.latency.max;

    // Error Rate
    const errorRate = container.querySelector('#error-rate');
    if (errorRate) errorRate.textContent = this._metrics.errorRate;
    const errorBar = container.querySelector('#error-bar-fill') as HTMLElement;
    if (errorBar) {
      const pct = Math.min(parseFloat(this._metrics.errorRate) * 10, 100);
      errorBar.style.width = `${pct}%`;
      errorBar.style.background = parseFloat(this._metrics.errorRate) > 1 ? 'var(--danger)' : 'var(--success)';
    }

    // Data Usage
    const meter = container.querySelector('app-meter');
    if (meter) {
      meter.setAttribute('value', String(this._metrics.dataUsage.value));
      meter.setAttribute('max', String(this._metrics.dataUsage.max));
      meter.setAttribute('label', this._metrics.dataUsage.label);
    }

    // Routes Table
    const tbody = container.querySelector('#routes-tbody');
    if (tbody) {
      tbody.innerHTML = this._metrics.trafficDetails.length > 0 ? this._metrics.trafficDetails.map((r: any) => `
        <tr class="explorable-row" data-type="routes" data-method="${r.method}" data-status="${r.status}" data-path="${r.path}">
          <td><span class="method-badge">${r.method}</span></td>
          <td class="path-cell" data-tooltip="${r.path}">${r.path}</td>
          <td><span class="status-cell ${r.status.startsWith('2') ? 'ok' : (r.status.startsWith('5') ? 'err' : 'warn')}">${r.status}</span></td>
          <td style="text-align: right; font-weight: 600;">${r.rps}</td>
        </tr>
      `).join('') : `<tr><td colspan="4" class="empty-table">No traffic detected in this period.</td></tr>`;
    }

    const statusDist = container.querySelector('#status-dist');
    if (statusDist) {
      const statusCodes = this._statusCodes || [];
      const total = statusCodes.reduce((acc: number, s: any) => acc + (s.count || s.value || 0), 0) || 1;
      statusDist.innerHTML = statusCodes.map((s: any) => {
        const val = s.count || s.value || 0;
        const label = s.name || s.label || 'Unknown';
        const color = label.startsWith('2') ? 'var(--success)' : (label.startsWith('5') ? 'var(--danger)' : 'var(--warning)');
        return `
          <div class="dist-row explorable-row" data-type="status_codes" data-status="${label}" style="padding: 4px; border-radius: 4px;">
            <div class="dist-info">
              <span class="dist-label">${label}</span>
              <span class="dist-value">${val.toLocaleString()}</span>
            </div>
            <div class="dist-bar-bg">
              <div class="dist-bar-fill" style="width: ${(val / total * 100).toFixed(1)}%; background: ${color}"></div>
            </div>
          </div>
        `;
      }).join('') || '<div class="empty-table" style="padding: 12px; font-size: 0.75rem;">No data</div>';
    }

    const methodsDist = container.querySelector('#methods-dist');
    if (methodsDist) {
      const methods = this._methods || [];
      const total = methods.reduce((acc: number, m: any) => acc + (m.count || m.value || 0), 0) || 1;
      methodsDist.innerHTML = methods.map((m: any) => {
        const val = m.count || m.value || 0;
        const label = m.name || m.label || 'Unknown';
        return `
          <div class="dist-row explorable-row" data-type="methods" data-method="${label}" style="padding: 4px; border-radius: 4px;">
            <div class="dist-info">
              <span class="dist-label">${label}</span>
              <span class="dist-value">${val.toLocaleString()}</span>
            </div>
            <div class="dist-bar-bg">
              <div class="dist-bar-fill" style="width: ${(val / total * 100).toFixed(1)}%"></div>
            </div>
          </div>
        `;
      }).join('') || '<div class="empty-table" style="padding: 12px; font-size: 0.75rem;">No data</div>';
    }
  }

  private changeRange(range: string) {
    this._timeRange = range;
    if (range !== 'custom') {
      localStorage.setItem('crate_gateway_range', range);
    }
    
    this.render();
    this.loadMetrics();
  }

  render() {
    if (!this.shadowRoot) return;

    this.shadowRoot.innerHTML = `
      <style>
        :host { 
          display: block; 
          font-family: var(--font-main); 
        }

        .dashboard-controls {
          display: flex;
          justify-content: flex-end;
          align-items: center;
          gap: 0.5rem;
          margin-bottom: 24px;
        }

        .grid {
          display: grid;
          grid-template-columns: repeat(6, 1fr);
          gap: 20px;
          margin-bottom: 24px;
        }

        .col-span-2 { grid-column: span 2; }
        .col-span-3 { grid-column: span 3; }
        .col-span-6 { grid-column: span 6; }

        .stat-card {
          height: 100%;
        }

        .stat-card {
          height: 100%;
        }
        .stat-header {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }

        .stat-title {
          font-size: 0.7rem;
          text-transform: uppercase;
          letter-spacing: 0.05em;
          color: var(--text-dim);
          font-weight: 700;
        }

        .trend {
          font-size: 0.75rem;
          font-weight: 700;
          padding: 2px 6px;
          border-radius: 4px;
        }
        .trend.up { color: var(--success); background: rgba(16, 185, 129, 0.1); }
        .trend.down { color: var(--danger); background: rgba(239, 68, 68, 0.1); }

        .stat-value {
          font-size: 2rem;
          font-weight: 800;
          color: var(--text-main);
          font-family: var(--font-sketch);
          margin-bottom: 12px;
        }

        .chart-container {
          height: 60px;
          width: 100%;
          margin-top: auto;
          pointer-events: none; /* Let clicks pass to the card */
        }

        .latency-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 8px 0;
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }
        .latency-row:last-child { border-bottom: none; }
        .latency-label { font-size: 0.75rem; color: var(--text-dim); }
        .latency-val { font-size: 1rem; font-weight: 700; color: var(--text-main); }

        .error-bar {
          height: 8px;
          background: rgba(255,255,255,0.05);
          border-radius: 4px;
          overflow: hidden;
          margin-top: 16px;
        }
        .error-fill {
          height: 100%;
          transition: width 0.5s ease-out;
        }

        .table-container {
          margin-top: 16px;
          overflow-x: auto;
        }

        table {
          width: 100%;
          border-collapse: collapse;
          font-size: 0.85rem;
        }

        th {
          text-align: left;
          padding: 12px 8px;
          color: var(--text-dim);
          border-bottom: 1px solid rgba(255,255,255,0.1);
          font-weight: 600;
          text-transform: uppercase;
          font-size: 0.7rem;
        }

        td {
          padding: 12px 8px;
          border-bottom: 1px solid rgba(255,255,255,0.05);
        }

        .method-badge {
          padding: 2px 6px;
          background: rgba(255,255,255,0.1);
          border-radius: 4px;
          font-size: 0.7rem;
          font-family: var(--font-mono);
          font-weight: 700;
        }

        .path-cell {
          font-family: var(--font-mono);
          color: var(--accent);
          white-space: nowrap;
          overflow: hidden;
          text-overflow: ellipsis;
          max-width: 400px;
        }

        .status-cell.ok { color: var(--success); }
        .status-cell.warn { color: var(--warning); }
        .status-cell.err { color: var(--danger); }

        .empty-table {
          padding: 48px;
          text-align: center;
          color: var(--text-dim);
        }

        .dist-row {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .dist-info {
          display: flex;
          justify-content: space-between;
          font-size: 0.75rem;
          font-weight: 600;
        }

        .dist-label { color: var(--text-dim); }
        .dist-value { color: var(--text-main); }

        .dist-bar-bg {
          height: 6px;
          background: rgba(255,255,255,0.05);
          border-radius: 3px;
          overflow: hidden;
        }

        .dist-bar-fill {
          height: 100%;
          background: var(--accent);
          border-radius: 3px;
          transition: width 0.3s ease-out;
          filter: url(#filt-sketch-${'metrics-page'.split('').reduce((a, b) => a + b.charCodeAt(0), 0)});
        }

        .loader {
          display: flex;
          justify-content: center;
          padding: 100px;
        }

        /* Global Scrollbar Override for consistency */
        ::-webkit-scrollbar {
          width: 12px;
          height: 12px;
        }

        ::-webkit-scrollbar-track {
          background: rgba(0, 0, 0, 0.2);
          border-left: 1px solid var(--border-sketch, rgba(255,255,255,0.1));
        }

        ::-webkit-scrollbar-thumb {
          background: rgba(245, 158, 11, 0.2);
          border-radius: 20px;
          border: 3px solid transparent;
          background-clip: content-box;
        }

        ::-webkit-scrollbar-thumb:hover {
          background: rgba(245, 158, 11, 0.5);
          background-clip: content-box;
        }
      </style>

      ${this._isLoading && this._isInitialLoad ? `
        <div class="loader">
          <div style="width: 40px; height: 40px; border: 3px solid rgba(245, 158, 11, 0.1); border-top-color: var(--accent); border-radius: 50%; animation: spin 1s linear infinite;"></div>
        </div>
        <style>
          @keyframes spin { to { transform: rotate(360deg); } }
        </style>
      ` : `
        <div class="dashboard-controls">
          <app-button variant="ghost" size="sm" id="btn-open-explorer" icon="search" style="margin-right: auto; border: 1px solid rgba(255,255,255,0.08);">Data Explorer</app-button>

          <div style="position: relative; display: flex; align-items: center; gap: 0.5rem;">
            <app-time-picker value="${this._timeRange}"></app-time-picker>
            
            <div style="width: 1px; height: 20px; background: rgba(255,255,255,0.08); margin: 0 4px; align-self: center;"></div>
            <app-button variant="ghost" size="sm" id="refresh-btn" icon="rotate-cw"></app-button>
          </div>
        </div>

        ${injectAesthetic('metrics-page')}

        <div class="grid">
          <app-card class="col-span-2 stat-card" data-explore-type="traffic">
            <div class="stat-header">
              <span class="stat-title">Request Volume</span>
              <div style="display:flex; gap: 8px; align-items:center;">
                <span id="traffic-trend" class="trend">${this._metrics.trafficTrend}</span>
              </div>
            </div>
            <div id="traffic-value" class="stat-value">${this._metrics.traffic}</div>
            <div class="chart-container">
              <app-line-chart chart-title="Request Volume" hide-axis="true"></app-line-chart>
            </div>
          </app-card>

          <app-card class="col-span-2 stat-card" data-explore-type="latency">
            <div class="stat-header">
              <span class="stat-title">Latency</span>
            </div>
            <div style="margin-top: 8px;">
              <div class="latency-row">
                <span class="latency-label">Typical (P50)</span>
                <span id="lat-min" class="latency-val">${this._metrics.latency.min}</span>
              </div>
              <div class="latency-row">
                <span class="latency-label">Average</span>
                <span id="lat-avg" class="latency-val">${this._metrics.latency.avg}</span>
              </div>
              <div class="latency-row">
                <span class="latency-label">Peak (P99)</span>
                <span id="lat-max" class="latency-val">${this._metrics.latency.max}</span>
              </div>
            </div>
          </app-card>

          <app-card class="col-span-2 stat-card" data-explore-type="error_rate">
            <div class="stat-header">
              <span class="stat-title">Error Rate</span>
            </div>
            <div id="error-rate" class="stat-value" style="color: ${parseFloat(this._metrics.errorRate) > 1 ? 'var(--danger)' : 'var(--success)'}">${this._metrics.errorRate}</div>
            <div class="error-bar">
              <div id="error-bar-fill" class="error-fill" style="width: ${Math.min(parseFloat(this._metrics.errorRate) * 10, 100)}%; background: ${parseFloat(this._metrics.errorRate) > 1 ? 'var(--danger)' : 'var(--success)'}"></div>
            </div>
          </app-card>

          <app-card class="col-span-2 stat-card" data-explore-type="data_usage">
            <div style="height: 100%; display: flex; flex-direction: column; align-items: center; justify-content: center; position: relative;">
              <app-meter 
                value="${this._metrics.dataUsage.value}" 
                max="${this._metrics.dataUsage.max}" 
                label="${this._metrics.dataUsage.label}">
              </app-meter>
              <div style="font-size: 0.75rem; color: var(--text-dim); margin-top: 12px; text-transform: uppercase; font-weight: 700; letter-spacing: 0.05em;">Data Transfer</div>
            </div>
          </app-card>

          <app-card class="col-span-2 stat-card" data-explore-type="methods">
            <div class="stat-header">
              <span class="stat-title">Method Distribution</span>
            </div>
            <div id="methods-dist" style="display: flex; flex-direction: column; gap: 12px; margin-top: 8px;">
              <!-- Populated by updateSurgicalUI -->
            </div>
          </app-card>

          <app-card class="col-span-2 stat-card" data-explore-type="status_codes">
            <div class="stat-header">
              <span class="stat-title">Status Distribution</span>
            </div>
            <div id="status-dist" style="display: flex; flex-direction: column; gap: 12px; margin-top: 8px;">
              <!-- Populated by updateSurgicalUI -->
            </div>
          </app-card>

          <app-card class="col-span-6 stat-card" data-explore-type="routes">
            <div class="stat-header">
              <span class="stat-title">Top Routes</span>
            </div>
            <div class="table-container">
              <table>
                <thead>
                  <tr>
                    <th style="width: 80px;">Method</th>
                    <th>Path</th>
                    <th style="width: 80px;">Status</th>
                    <th style="width: 80px; text-align: right;">RPS</th>
                  </tr>
                </thead>
                <tbody id="routes-tbody">
                  ${this._metrics.trafficDetails.length > 0 ? this._metrics.trafficDetails.map((r: any) => `
                    <tr class="explorable-row" data-type="routes" data-method="${r.method}" data-status="${r.status}" data-path="${r.path}">
                      <td><span class="method-badge">${r.method}</span></td>
                      <td class="path-cell" data-tooltip="${r.path}">${r.path}</td>
                      <td><span class="status-cell ${r.status.startsWith('2') ? 'ok' : (r.status.startsWith('5') ? 'err' : 'warn')}">${r.status}</span></td>
                      <td style="text-align: right; font-weight: 600;">${r.rps}</td>
                    </tr>
                  `).join('') : `<tr><td colspan="4" class="empty-table">No traffic detected.</td></tr>`}
                </tbody>
              </table>
            </div>
          </app-card>
        </div>
        
        <app-chart-explorer></app-chart-explorer>
      `}
    `;

    this.bindEvents();
  }

  bindEvents() {
    this.shadowRoot?.querySelector('app-time-picker')?.addEventListener('range-change', (e: Event) => {
      const detail = (e as CustomEvent).detail;
      if (detail.range === 'custom' && detail.customRange) {
        this._customRange = detail.customRange;
      }
      this.changeRange(detail.range);
    });

    this.shadowRoot?.querySelector('#refresh-btn')?.addEventListener('click', () => this.loadMetrics());
    
    this.shadowRoot?.querySelector('#btn-open-explorer')?.addEventListener('click', () => {
        const explorer = this.shadowRoot?.querySelector('app-chart-explorer') as any;
        if (explorer) {
            this._metricsController.stopPolling();
            explorer.open('traffic', this._orgId, this._timeRange, this._customRange, this._methods, this._statusCodes, {});
            explorer.addEventListener('explorer-closed', () => {
                this._metricsController.startPolling();
            }, { once: true });
        }
    });

    if (!this._isInitialLoad) {
      this.updateSurgicalUI();
    }
  }
}

if (!customElements.get('gateway-metrics')) {
  customElements.define('gateway-metrics', GatewayMetrics);
}
